// import React from "react";
// import RevenueChart from "../widgets/RevenueChart";
// import OrdersTable from "../widgets/OrdersTable";
// import MostSold from "../widgets/MostSold";
// import CardTile from "../widgets/CardTile";

// const DashboardPage: React.FC = () => {
//   return (
//     <div className="dashboard-grid">
//       {/* Top small cards */}
//       <div className="row g-3 mb-3">
//         <div className="col-md-4"><CardTile title="Todays Sales" value="#20.4K" subtitle="We have sold 123 items" /></div>
//         <div className="col-md-4"><CardTile title="Todays Revenue" value="#8.2K" subtitle="Available to payout" /></div>
//         <div className="col-md-4"><CardTile title="In Escrow" value="#18.2K" subtitle="Available to payout" /></div>
//       </div>

//       {/* Chart + Most sold */}
//       <div className="row g-3">
//         <div className="col-lg-8">
//           <div className="card p-3 chart-card">
//             <div className="d-flex justify-content-between align-items-center mb-3">
//               <div><h6 className="mb-0">Total Revenue</h6><small className="text-success">↑ 5% than last month</small></div>
//               <div className="legend"><span className="dot profit"></span> Profit &nbsp; <span className="dot loss"></span> Loss</div>
//             </div>
//             <RevenueChart />
//           </div>
//         </div>

//         <div className="col-lg-4">
//           <MostSold />
//         </div>
//       </div>

//       {/* Latest Orders full width */}
//       <div className="row mt-4">
//         <div className="col-12">
//           <OrdersTable />
//         </div>
//       </div>
//     </div>
//   );
// };

// export default DashboardPage;



import React, { useEffect, useState } from "react";
import { useGlobalContext } from "../context/globalContext";
import { useTheme } from "../context/ThemeContext";
import RevenueChart from "../widgets/RevenueChart";
import OrdersTable from "../widgets/OrdersTable";
import MostSold from "../widgets/MostSold";
import CardTile from "../widgets/CardTile";

const DashboardPage: React.FC = () => {
  const { orders, getOrders } = useGlobalContext();
  const { dark } = useTheme();

  const [todaysSales, setTodaysSales] = useState<number>(0);
  const [todaysRevenue, setTodaysRevenue] = useState<number>(0);
  const [totalRevenue, setTotalRevenue] = useState<number>(0);
  const [totalSales, setTotalSales] = useState<number>(0);
  const [dateRange, setDateRange] = useState<string>("");

  useEffect(() => {
    getOrders();
  }, []);

  useEffect(() => {
    if (orders && orders.length > 0) {
      const today = new Date().toDateString();

      let todaySales = 0;
      let todayRevenue = 0;
      let overallRevenue = 0;
      let overallSales = 0;

      const sortedOrders = [...orders].sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );

      const firstOrderDate = new Date(sortedOrders[0].createdAt);
      const lastOrderDate = new Date();

      const formattedFirstDate = firstOrderDate.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
      const formattedLastDate = lastOrderDate.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });

      setDateRange(`${formattedFirstDate} — ${formattedLastDate}`);

      orders.forEach((order) => {
        const orderDate = new Date(order.createdAt).toDateString();
        overallRevenue += order.totalAmount;

        const items = order?.orderItems ?? [];
        const orderTotalQuantity = items.reduce(
          (sum, item) => sum + (item?.quantity ?? 0),
          0
        );

        overallSales += orderTotalQuantity;

        if (orderDate === today) {
          todayRevenue += order.totalAmount;
          todaySales += orderTotalQuantity;
        }
      });

      setTodaysSales(todaySales);
      setTodaysRevenue(todayRevenue);
      setTotalRevenue(overallRevenue);
      setTotalSales(overallSales);
    }
  }, [orders]);

  const formatInMillions = (amount: number): string => {
    if (amount >= 1_000_000) {
      return `₦${(amount / 1_000_000).toFixed(2)}M`;
    }
    return `₦${amount.toLocaleString()}`;
  };

  const bgClass = dark ? "bg-dark text-light" : "bg-light text-dark";
  const cardClass = dark ? "bg-secondary text-light" : "bg-white text-dark";
  const chartCardClass = dark
    ? "bg-dark text-light border-0 shadow rounded-4"
    : "bg-white text-dark border-0 shadow rounded-4";

  return (
    <div className={`dashboard-grid min-vh-100 p-3 ${bgClass}`}>
      {/* Top cards */}
      <div className="row g-3 mb-3">
        <div className="col-12 col-sm-6 col-lg-4">
          <CardTile
            title="Today's Sales"
            value={`${todaysSales.toLocaleString()} items`}
            subtitle="Items sold today"
            className={`${cardClass} shadow rounded-4`}
          />
        </div>

        <div className="col-12 col-sm-6 col-lg-4">
          <CardTile
            title="Today's Revenue"
            value={formatInMillions(todaysRevenue)}
            subtitle="Revenue from today's orders"
            className={`${cardClass} shadow rounded-4`}
          />
        </div>

        <div className="col-12 col-lg-4">
          <CardTile
            title="Total Sales So Far"
            value={formatInMillions(totalRevenue)}
            subtitle={`From ${dateRange}`}
            className={`${
              dark ? "bg-success text-light" : "bg-success text-white"
            } shadow rounded-4`}
          />
        </div>
      </div>

      {/* Chart + Most Sold */}
      <div className="row g-3">
        <div className="col-12 col-lg-8">
          <div className={`card p-3 ${chartCardClass}`}>
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
              <div>
                <h6 className="mb-0 text-uppercase text-muted">
                  Total Revenue
                </h6>
                <small
                  className={
                    orders && orders.length > 0
                      ? "text-success fw-bold"
                      : "text-warning"
                  }
                >
                  {orders && orders.length > 0
                    ? "↑ Active Sales Trend"
                    : "Waiting for data"}
                </small>
              </div>
              <div className="legend small">
                <span className="dot bg-success"></span> Profit &nbsp;
                <span className="dot bg-danger"></span> Loss
              </div>
            </div>
            <RevenueChart orders={orders || []} />
            <p className="mt-2 text-muted small">Sales period: {dateRange}</p>
          </div>
        </div>

        <div className="col-12 col-lg-4">
          <MostSold />
        </div>
      </div>

      {/* Latest Orders */}
      <div className="row mt-4">
        <div className="col-12">
          <OrdersTable orders={orders || []} />
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;

